// ─────────────────────────────────────────────────────────────────────────
// presets.ts — ready-made theme token sets (the three visual directions).
// Apply one via configure({ theme: themePresets.premium }) or el.config; each
// is pure --sc-* custom properties, so no markup/structure changes. The package
// default already matches `refined`.
// ─────────────────────────────────────────────────────────────────────────

import type { ThemeTokens } from './schema.js';

/** Refined Neutral — cobalt, system sans, flat. Matches the package default. */
const refined: ThemeTokens = {
  '--sc-accent': '#2563eb',
  '--sc-accent-ink': '#1e40af',
  '--sc-ink': '#0f172a',
  '--sc-ink-soft': '#334155',
  '--sc-surface': '#ffffff',
  '--sc-paper': '#f8f9fb',
  '--sc-muted': '#64748b',
  '--sc-edge': '#e2e5ea',
  '--sc-positive': '#16803c',
  '--sc-radius': '8px',
  '--sc-font': '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
};

/** Premium Elevated — indigo, Inter, soft shadows + gradient button. */
const premium: ThemeTokens = {
  '--sc-accent': '#6366f1',
  '--sc-accent-ink': '#4f46e5',
  '--sc-ink': '#0f0d1a',
  '--sc-ink-soft': '#3b3556',
  '--sc-surface': '#ffffff',
  '--sc-paper': '#f5f3ff',
  '--sc-muted': '#6b6585',
  '--sc-edge': '#e4e0f0',
  '--sc-positive': '#059669',
  '--sc-radius': '12px',
  '--sc-font': '"Inter", -apple-system, system-ui, sans-serif',
  '--sc-shell-shadow': '0 4px 24px rgba(15,13,26,0.06), 0 1px 3px rgba(15,13,26,0.04)',
  '--sc-btn-bg': 'linear-gradient(to bottom, #6366f1, color-mix(in srgb, #6366f1 85%, black))',
};

/** Warm Trust — emerald, Avenir, gradient hero. Approachable / eco / residential. */
const trust: ThemeTokens = {
  '--sc-accent': '#0d9463',
  '--sc-accent-ink': '#087b52',
  '--sc-ink': '#0c1f17',
  '--sc-ink-soft': '#1c4a38',
  '--sc-surface': '#ffffff',
  '--sc-paper': '#f0faf5',
  '--sc-muted': '#5a7a6d',
  '--sc-edge': '#d4e5dc',
  '--sc-positive': '#0d9463',
  '--sc-radius': '10px',
  '--sc-font': '"Avenir Next", "Avenir", -apple-system, system-ui, sans-serif',
  '--sc-hero-bg':
    'linear-gradient(135deg, color-mix(in srgb, #0d9463 6%, #ffffff), color-mix(in srgb, #0d9463 3%, #ffffff))',
};

export const themePresets = { refined, premium, trust } as const;
export type ThemePresetName = keyof typeof themePresets;
